import { getBearerToken } from '@/src/auth/token-storage';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';
type ConfiguredLogLevel = LogLevel | 'silent';
type Runtime = 'browser' | 'server' | 'test';
type SerializableValue = string | number | boolean | null | SerializableValue[] | { [key: string]: SerializableValue };

type SerializedError = {
  name?: string;
  message?: string;
  stack?: string;
  cause?: SerializableValue;
  details?: SerializableValue;
};

export type LogEvent = {
  timestamp: string;
  level: LogLevel;
  message: string;
  runtime: Runtime;
  environment: string;
  context?: SerializableValue;
  error?: SerializedError;
};

export type LogTransport = {
  name: string;
  emit: (event: LogEvent) => void;
};

const logLevelPriority: Record<ConfiguredLogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 50,
};

const maxDepth = 5;
const maxObjectKeys = 40;
const sensitiveKeyPattern = /token|authorization|cookie|password|secret|jwt|api[-_]?key|credential|session/i;
const sensitiveValuePattern = /token|search|password|authorization|cookie|secret|jwt|api[-_]?key|credential|session/i;
const authorizationValuePattern = /\b(authorization)\s*[:=]\s*\S+\s+[^\s,;&]+/gi;
const sensitiveKeyValuePattern = new RegExp(`\\b(${sensitiveValuePattern.source})\\s*[:=]\\s*([^\\s,;&]+)`, 'gi');
const absoluteUrlPattern = /\bhttps?:\/\/[^\s?#]+(?:\?[^\s#]*)?(?:#[^\s]*)?/gi;
const redactedValue = '[REDACTED]';

function getEnvironment() {
  return process.env.NODE_ENV ?? 'development';
}

function getRuntime(): Runtime {
  if (getEnvironment() === 'test') {
    return 'test';
  }

  return typeof window === 'undefined' ? 'server' : 'browser';
}

function normalizeLogLevel(value: string | undefined): ConfiguredLogLevel | null {
  if (!value) {
    return null;
  }

  const normalized = value.toLowerCase();

  if (normalized === 'debug' || normalized === 'info' || normalized === 'warn' || normalized === 'error' || normalized === 'silent') {
    return normalized;
  }

  return null;
}

function getConfiguredLogLevel(): ConfiguredLogLevel {
  return normalizeLogLevel(process.env.NEXT_PUBLIC_LOG_LEVEL)
    ?? normalizeLogLevel(process.env.LOG_LEVEL)
    ?? (getEnvironment() === 'production' ? 'info' : 'debug');
}

function shouldLog(level: LogLevel) {
  return logLevelPriority[level] >= logLevelPriority[getConfiguredLogLevel()];
}

function sanitizeLogText(value: string): string {
  return value
    .replace(absoluteUrlPattern, (match) => stripUrlQueryAndHash(match))
    .replace(authorizationValuePattern, (_match, key: string) => `${key}=${redactedValue}`)
    .replace(sensitiveKeyValuePattern, (_match, key: string) => `${key}=${redactedValue}`);
}

function stripUrlQueryAndHash(value: string): string {
  try {
    const url = new URL(value);
    url.search = '';
    url.hash = '';
    return url.toString().replace(/\/$/, url.pathname === '/' ? '/' : '');
  } catch {
    return value.split(/[?#]/, 1)[0] || value;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function serializeValue(value: unknown, depth = 0, seen = new WeakSet<object>()): SerializableValue {
  if (value === null || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return sanitizeLogText(value);
  }

  if (value === undefined) {
    return '[undefined]';
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (typeof value === 'symbol' || typeof value === 'function') {
    return `[${typeof value}]`;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof Error) {
    return serializeError(value, depth, seen);
  }

  if (seen.has(value)) {
    return '[Circular]';
  }

  if (depth >= maxDepth) {
    return '[MaxDepth]';
  }

  seen.add(value);

  if (Array.isArray(value)) {
    const serialized = value.slice(0, maxObjectKeys).map((entry) => serializeValue(entry, depth + 1, seen));

    if (value.length > maxObjectKeys) {
      serialized.push(`[${value.length - maxObjectKeys} more items]`);
    }

    return serialized;
  }

  const source = isPlainObject(value) ? value : Object.fromEntries(
    Object.entries(value as Record<string, unknown>),
  );
  const entries = Object.entries(source);
  const serialized: Record<string, SerializableValue> = {};

  for (const [key, entryValue] of entries.slice(0, maxObjectKeys)) {
    serialized[key] = sensitiveKeyPattern.test(key) ? redactedValue : serializeValue(entryValue, depth + 1, seen);
  }

  if (entries.length > maxObjectKeys) {
    serialized.__truncatedKeys = entries.length - maxObjectKeys;
  }

  return serialized;
}

function serializeError(error: unknown, depth = 0, seen = new WeakSet<object>()): SerializedError {
  if (error instanceof Error) {
    const serialized: SerializedError = {
      name: error.name,
      message: sanitizeLogText(error.message),
    };

    if ((getEnvironment() !== 'production' || getRuntime() === 'browser') && error.stack) {
      serialized.stack = sanitizeLogText(error.stack);
    }

    if ('cause' in error && error.cause !== undefined) {
      serialized.cause = serializeValue(error.cause, depth + 1, seen);
    }

    return serialized;
  }

  if (typeof error === 'string') {
    return { message: sanitizeLogText(error) };
  }

  return { details: serializeValue(error, depth, seen) };
}

function createLogEvent(level: LogLevel, message: string, error?: unknown, context?: Record<string, unknown>): LogEvent {
  return {
    timestamp: new Date().toISOString(),
    level,
    message,
    runtime: getRuntime(),
    environment: getEnvironment(),
    ...(context ? { context: serializeValue(context) } : {}),
    ...(error !== undefined ? { error: serializeError(error) } : {}),
  };
}

export function createConsoleLogTransport(): LogTransport {
  return {
    name: 'console',
    emit(event) {
      const output = (console[event.level] ?? console.log).bind(console);
      output(`[${event.level.toUpperCase()}] ${event.message}`, event);
    },
  };
}

type RemoteTelemetryOptions = {
  fetcher?: typeof fetch;
  getToken?: () => string | null;
  random?: () => number;
  releaseSha?: string;
  errorSampleRate?: number;
  warnSampleRate?: number;
  operationalCategoryPattern?: RegExp;
  route?: () => string;
  correlationId?: () => string;
};

function samplingRate(value: string | undefined, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback;
}

function stableCategory(event: LogEvent) {
  const context = isPlainObject(event.context) ? event.context : null;
  const configured = context?.errorCategory;
  if (
    typeof configured === 'string' &&
    /^browser\.[a-z][a-z0-9._-]{0,87}$/.test(configured)
  ) {
    return configured;
  }
  const knownErrorNames: Record<string, string> = {
    Error: 'browser.error.generic',
    TypeError: 'browser.error.type_error',
    RangeError: 'browser.error.range_error',
    SyntaxError: 'browser.error.syntax_error',
    AbortError: 'browser.error.abort_error',
  };
  return knownErrorNames[event.error?.name ?? ''] ?? 'browser.error.unknown';
}

function stackFrames(event: LogEvent) {
  return event.error?.stack
    ?.split('\n')
    .filter((line) => /^\s*at\s+/i.test(line))
    .slice(0, 40)
    .map((line) => line.replace(/https?:\/\/[^\s)]+/gi, (rawUrl) => {
      try {
        const pathname = new URL(rawUrl).pathname;
        return pathname.startsWith('/_next/static/')
          ? pathname
          : '[REDACTED_URL]';
      } catch {
        return '[REDACTED_URL]';
      }
    }).slice(0, 500));
}

export function createRemoteBrowserTelemetryTransport(
  options: RemoteTelemetryOptions = {},
): LogTransport {
  const fetcher = options.fetcher ?? globalThis.fetch;
  const tokenProvider = options.getToken ?? getBearerToken;
  const random = options.random ?? Math.random;
  const releaseSha = options.releaseSha ?? process.env.NEXT_PUBLIC_SPARKONE_RELEASE_SHA ?? '';
  const errorSampleRate = options.errorSampleRate
    ?? samplingRate(process.env.NEXT_PUBLIC_BROWSER_TELEMETRY_ERROR_SAMPLE_RATE, 1);
  const warnSampleRate = options.warnSampleRate
    ?? samplingRate(process.env.NEXT_PUBLIC_BROWSER_TELEMETRY_WARN_SAMPLE_RATE, 0.1);
  const route = options.route ?? (() => window.location.pathname);
  const operationalCategoryPattern = options.operationalCategoryPattern
    ?? /^browser\.auth\.lifecycle\./;
  const correlationId = options.correlationId ?? (() => `telemetry_${crypto.randomUUID()}`);
  const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? 'http://localhost:3001';

  return {
    name: 'remote-browser-telemetry',
    emit(event) {
      if (
        event.runtime !== 'browser'
        || !/^[a-f0-9]{40,64}$/i.test(releaseSha)
      ) return;
      const context = isPlainObject(event.context) ? event.context : null;
      const category = stableCategory(event);
      const operationalEvent = operationalCategoryPattern.test(category);
      if (
        event.level !== 'warn'
        && event.level !== 'error'
        && !(event.level === 'info' && operationalEvent)
      ) return;
      const rate = operationalEvent
        ? 1
        : event.level === 'error'
          ? errorSampleRate
          : warnSampleRate;
      if (random() >= rate) return;
      const token = tokenProvider();
      if (!token) return;
      const contextualRequestId = context?.requestId;
      const requestId = typeof contextualRequestId === 'string'
        && /^[A-Za-z0-9._:-]{1,128}$/.test(contextualRequestId)
        ? contextualRequestId
        : correlationId();
      const frames = stackFrames(event);
      const payload = {
        severity: event.level.toUpperCase(),
        errorCategory: category,
        route: route().split(/[?#]/, 1)[0].slice(0, 240) || '/',
        releaseSha,
        occurredAt: event.timestamp,
        ...(event.error?.name ? { errorName: event.error.name.slice(0, 96) } : {}),
        ...(frames?.length ? { stackFrames: frames } : {}),
      };

      void fetcher(`${apiBaseUrl}/telemetry/browser`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-request-id': requestId,
        },
        body: JSON.stringify(payload),
      }).catch(() => undefined);
    },
  };
}

export function createCompositeLogTransport(
  transports: LogTransport[],
): LogTransport {
  return {
    name: transports.map(({ name }) => name).join('+'),
    emit(event) {
      transports.forEach((transport) => transport.emit(event));
    },
  };
}

type Logger = {
  debug: (message: string, context?: Record<string, unknown>) => void;
  info: (message: string, context?: Record<string, unknown>) => void;
  warn: (message: string, context?: Record<string, unknown>) => void;
  error: (message: string, error?: unknown, context?: Record<string, unknown>) => void;
};

type LoggerOptions = {
  transport?: LogTransport;
};

export function createLogger({ transport = createConsoleLogTransport() }: LoggerOptions = {}): Logger {
  function emit(level: LogLevel, message: string, error?: unknown, context?: Record<string, unknown>) {
    if (!shouldLog(level)) {
      return;
    }

    transport.emit(createLogEvent(level, message, error, context));
  }

  return {
    debug(message: string, context?: Record<string, unknown>) {
      emit('debug', message, undefined, context);
    },
    info(message: string, context?: Record<string, unknown>) {
      emit('info', message, undefined, context);
    },
    warn(message: string, context?: Record<string, unknown>) {
      emit('warn', message, undefined, context);
    },
    error(message: string, error?: unknown, context?: Record<string, unknown>) {
      emit('error', message, error, context);
    },
  };
}

export const logger = createLogger({
  transport: createCompositeLogTransport([
    createConsoleLogTransport(),
    createRemoteBrowserTelemetryTransport(),
  ]),
});
